/**
 * 部署・担当者フィルターの hydration（初期反映・再反映）に関する判断ロジック。
 *
 * useGlobalDepartmentAccountFilter の useLayoutEffect から「次に何をすべきか」の
 * 分岐判断だけを純粋関数として切り出したもの。副作用（state 更新・localStorage 操作・
 * サーバー同期）はフック側が担い、ここでは入力フラグから実行すべきアクションを決めるだけ。
 *
 * - 副作用を持たないため単体テストで網羅でき、分岐の意図がレビューしやすい
 * - フック側は決定されたアクションを実行するだけになり、責務が分離される（SRP）
 */

/** hydration 時に実行し得るアクション。 */
export type HydrationAction =
  /** 何もしない（既に反映済みで再反映の必要がない）。 */
  | "none"
  /** サーバー（user.filterConditions）の内容を反映する。 */
  | "hydrateFromServer"
  /** localStorage に保存された内容を反映する。 */
  | "hydrateFromLocalStorage"
  /** localStorage の内容をサーバーへ移行しつつ、localStorage の内容を反映する。 */
  | "migrateFromLocalStorage"
  /** localStorage の内容を反映しつつ、保留中のサーバー同期を再試行する。 */
  | "retryPendingSync";

/** hydration アクションを決めるための入力フラグ。 */
export type HydrationContext = {
  /** 既に一度 hydration 済みか（初回かどうかの判定）。 */
  alreadyHydrated: boolean;
  /** 前回反映時から user.filterConditions が変化したか。 */
  filterConditionsChanged: boolean;
  /** サーバー側に global フィルターが存在するか。 */
  hasServerFilter: boolean;
  /** localStorage に保存されたフィルターが存在するか。 */
  hasSavedFilter: boolean;
  /** サーバー同期が保留中（pending）か。 */
  hasPendingSync: boolean;
  /** localStorage → サーバーへの移行を既に試みたか。 */
  hasAttemptedMigration: boolean;
};

/**
 * 入力フラグから次に実行すべき hydration アクションを決定する。
 *
 * 初回（!alreadyHydrated）:
 *   1. サーバーにフィルターがあればサーバーを反映
 *   2. localStorage にフィルターがあり未移行なら移行（migrate）
 *   3. 保留中の同期があれば再試行（retry）
 *   4. localStorage にだけ値があれば反映
 *   5. いずれも無ければサーバーを反映（＝フィルター無しの初期状態）
 *
 * 再実行（alreadyHydrated）:
 *   filterConditions が変化し、かつサーバー反映が妥当なときだけ再反映する。
 *   （localStorage/保留中の同期が優先される状況では何もしない）
 */
export const decideHydrationAction = (context: HydrationContext): HydrationAction => {
  if (context.alreadyHydrated) {
    if (!context.filterConditionsChanged) {
      return "none";
    }
    if (context.hasServerFilter) {
      return "hydrateFromServer";
    }
    if (!context.hasSavedFilter && !context.hasPendingSync) {
      return "hydrateFromServer";
    }
    return "none";
  }

  if (context.hasServerFilter) {
    return "hydrateFromServer";
  }

  if (context.hasSavedFilter || context.hasPendingSync) {
    if (context.hasSavedFilter && !context.hasAttemptedMigration) {
      return "migrateFromLocalStorage";
    }
    if (context.hasPendingSync) {
      return "retryPendingSync";
    }
    return "hydrateFromLocalStorage";
  }

  return "hydrateFromServer";
};
